import { useState, useEffect } from "react";
import { useAtom } from "@dbeining/react-atom";
import { userAtom } from "../../atoms/userAtom";
import { AppointmentType } from "../../constants/AppointmentTypeIds";
import { showToastSuccess, showToastError } from "../../utils";
import { calendarEventService, emailTemplateService, userService } from "../../services";
import { ICalendarEvent } from "../../customTypings/calendar/CalendarEvent";
import mapEmailBody from "../../pages/Task/Mapping";
import { useCalendarData } from "./useCalendarData";
import { useEmailTemplate } from "./useEmailTemplate";
import { formatDateForForm, formatTimeForForm, createUTCDate, getRoundedDate } from "../../utils/dateUtils";

export const useEventAdd = (
  taskId?: string,
  isTaskEvent?: boolean,
  identityIds?: string[],
) => {
  const [alertVariant, setAlertVariant] = useState("danger");
  const currentUser = useAtom(userAtom);
  const actioner = `${currentUser?.identity.displayName}: ${currentUser?.identity.email}`;
  const [adminEmail, setAdminEmail] = useState("");

  const { allAppointmentTypes, allIdentities } = useCalendarData();
  const { emailTemplate, eventTemplate, createEmailArray } = useEmailTemplate("Event creation");

  useEffect(() => {
    userService.getAdminEmail().then((response: any) => { 
      setAdminEmail(response) 
    });
  }, []);

  const handleSubmit = async (values: any, { setStatus, setSubmitting }: any) => {
    setStatus();
    setSubmitting(true);
    
    const newCalendarEntry: ICalendarEvent = {
      id: undefined,
      taskId: taskId ?? undefined,
      title: values.title,
      description: values.description,
      appointmentTypeId: taskId || isTaskEvent ? AppointmentType.task : values.appointmentTypeId,
      startDate: createUTCDate(values.startDate, values.startTime),
      endDate: createUTCDate(values.endDate, values.endTime),
      isAllDay: values.isAllDay,
      identityIds: values.selectedIdentityIds,
      color: values.color,
    };

    try {
      const response = await calendarEventService.add(newCalendarEntry);
      setSubmitting(false);
      
      if (response.status !== "Failure") {
        // Send emails to personnel groups
        const personals = allIdentities?.filter(user => 
          newCalendarEntry.identityIds?.includes(user.value)
        ).map((item: any) => ({
          email: item?.label.split(": ")[1],
        }));
        
        const receiverEmails = await createEmailArray(eventTemplate, personals, currentUser?.identity.email ?? "", adminEmail);
        const updatedBody = await mapEmailBody({ 
          body: emailTemplate.body, 
          id: response.id, 
          actioner: actioner 
        });
        const updatedSubject = await mapEmailBody({ 
          body: emailTemplate.subject, 
          id: response.id, 
          actioner: actioner 
        });
        
        if (receiverEmails?.length > 0) {
          await emailTemplateService.sendAutoEmail(updatedSubject, updatedBody, receiverEmails);
        }
        showToastSuccess("Event created successfully");
        return true;
      } else {
        setAlertVariant("danger");
        showToastError(response.message);
        return false;
      }
    } catch (error: any) {
      setAlertVariant("danger");
      if (error.status === 400 && error.errors) {
        Object.keys(error.errors).forEach((key) => {
          const messages = error.errors[key];
          if (Array.isArray(messages)) {
            messages.forEach((message) => showToastError(message));
          }
        });
      } else {
        showToastError("Something went wrong. Please try again.");
      }
      setSubmitting(false);
      return false;
    }
  };

  const getInitialValues = (dateTimeStart?: Date, dateTimeEnd?: Date, isAllDay?: boolean) => ({
    title: "",
    description: "",
    appointmentTypeId: taskId || isTaskEvent ? AppointmentType.task : "",
    selectedIdentityIds: identityIds as string[],
    startDate: formatDateForForm(dateTimeStart, new Date()),
    startTime: formatTimeForForm(
      dateTimeStart,
      getRoundedDate(30, new Date())
    ),
    endDate: formatDateForForm(dateTimeEnd, new Date()),
    endTime: formatTimeForForm(
      dateTimeEnd,
      getRoundedDate(30, new Date())
    ),
    notes: "",
    isAllDay: isAllDay ?? false,
    color: undefined,
  });

  return {
    alertVariant,
    currentUser,
    allAppointmentTypes,
    allIdentities,
    handleSubmit,
    getInitialValues,
  };
};
